import Navbar from 'organisms/Navbar';
import NavigationSidebar from 'organisms/NavigationSidebar';
import MobileBottomNav from 'organisms/MobileBottomNav';
import { NavigationMenuProvider } from 'context/NavigationMenuContext';
import useDragOffset from 'hooks/useDragOffset';
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { Sparkles, X } from 'lucide-react';
import { AGENT_PAGE, AgentPanelProvider, VARIANT_KEY } from 'components/AgentChat/AgentPanelContext';

const AgentChatbot = dynamic(
  () => import('components/AgentChat/AgentChatbot'),
  { ssr: false, loading: () => null }
);

// The agent opens in one of three views: docked beside the page, a pop-up window, or its own page
// (/agent, components/AgentChat/README.md). The first two are kept here; the page is a route.
const SIDEBAR = 'sidebar';
const POPUP = 'popup';
const FULL = 'full';

function storedVariant() {
  try {
    return localStorage.getItem(VARIANT_KEY) === POPUP ? POPUP : SIDEBAR;
  } catch (error) {
    return SIDEBAR;
  }
}

const Layout = ({ children }) => {
  const router = useRouter();
  const isAgentPage = router.pathname === AGENT_PAGE;
  const [showBI, setShowBI] = useState(false);
  const [variant, setVariant] = useState(SIDEBAR);

  const BI_WIDTH_KEY = 'bi_sidebar_width';
  const MIN_WIDTH = 360;
  const MAX_WIDTH = 900;
  const DEFAULT_WIDTH = 420;

  const [sidebarWidth, setSidebarWidth] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_WIDTH;
    const stored = parseInt(localStorage.getItem(BI_WIDTH_KEY), 10);
    return stored && stored >= MIN_WIDTH && stored <= MAX_WIDTH ? stored : DEFAULT_WIDTH;
  });

  useEffect(() => {
    setVariant(storedVariant());
  }, []);

  const changeVariant = useCallback((next) => {
    if (next === FULL) {
      setShowBI(false);
      router.push(AGENT_PAGE);
      return;
    }
    setVariant(next);
    setShowBI(true);
    try {
      localStorage.setItem(VARIANT_KEY, next);
    } catch (error) {
      // The choice is a convenience; without storage the panel simply opens docked next time.
    }
    if (isAgentPage) {
      if (window.history.length > 1) router.back();
      else router.push('/');
    }
  }, [router, isAgentPage]);

  const panelContext = useMemo(() => ({
    openPanel: changeVariant,
    closeFullPage: () => (window.history.length > 1 ? router.back() : router.push('/')),
  }), [changeVariant, router]);

  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);

  const onDragStart = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragStartWidth.current = sidebarWidth;
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
  }, [sidebarWidth]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isDragging.current) return;
      const delta = dragStartX.current - e.clientX;
      const next = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, dragStartWidth.current + delta));
      setSidebarWidth(next);
    };
    const onMouseUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setSidebarWidth((w) => {
        localStorage.setItem(BI_WIDTH_KEY, String(w));
        return w;
      });
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const { offset: launcherOffset, consumeDrag: consumeLauncherDrag, dragHandlers: launcherDragHandlers } = useDragOffset();

  const isOpen = showBI && !isAgentPage;
  const isDocked = isOpen && variant === SIDEBAR;
  const isPopup = isOpen && variant === POPUP;
  const closeAgent = () => setShowBI(false);

  const chat = (
    <AgentChatbot variant={variant} onChangeVariant={changeVariant} onClose={closeAgent} />
  );

  return (
    <NavigationMenuProvider>
    <AgentPanelProvider value={panelContext}>
    {/* On desktop the docked agent panel is a column beside the page, so the page always takes
        exactly the remaining width (and resizes with the panel). On mobile it is an overlay. */}
    <div className="flex h-screen overflow-hidden">
      <div className="flex h-full min-w-0 flex-1 flex-col">
        <Navbar />
        <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto scrollbar pb-[calc(56px+env(safe-area-inset-bottom))] md:pb-0">
          <div className="flex w-full relative h-fit">
            <div className="hidden md:block" style={{ zIndex: 0 }}>
              <NavigationSidebar />
            </div>
            <div className="h-fit flex-1 min-w-0 w-full md:w-[78%]">{children}</div>
          </div>
        </div>
      </div>

      <MobileBottomNav />

      {!isAgentPage && (
        // The wrapper carries the drag offset, so the button's own show/hide transform still applies.
        <div
          className="pointer-events-none fixed right-4 bottom-[calc(68px+env(safe-area-inset-bottom))] md:right-6 md:bottom-6 z-[56]"
          style={{ transform: `translate(${launcherOffset.x}px, ${launcherOffset.y}px)` }}
        >
          <button
            type="button"
            {...launcherDragHandlers}
            onClick={() => {
              if (!consumeLauncherDrag()) setShowBI((v) => !v);
            }}
            aria-label={isOpen ? 'Close Afto Agent' : 'Open Afto Agent'}
            title="Drag to move"
            className={`flex touch-none select-none cursor-grab active:cursor-grabbing items-center gap-2 rounded-2xl bg-agent-deep py-2.5 pl-2.5 pr-4 font-agent text-xs font-bold text-white shadow-agent-float transition-all duration-300 hover:bg-agent-deeper focus:outline-none focus-visible:ring-2 focus-visible:ring-agent-brand focus-visible:ring-offset-2 ${
              isDocked ? 'pointer-events-auto md:pointer-events-none md:translate-y-4 md:opacity-0' : 'pointer-events-auto opacity-100'
            }`}
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-[9px] rounded-bl-[3px] bg-agent-glow text-agent-brand-ink">
              {isOpen ? <X className="h-4 w-4" aria-hidden="true" /> : <Sparkles className="h-4 w-4" aria-hidden="true" />}
            </span>
            {isOpen ? 'Close' : 'Ask Afto'}
          </button>
        </div>
      )}

      {/* Docked: a resizable column beside the page on desktop, a slide-over on mobile. */}
      <div
        className={`z-[55] flex flex-col overflow-hidden border-l border-agent-line bg-agent-canvas shadow-[-12px_0_30px_rgba(16,36,58,0.06)] fixed top-0 right-0 h-full transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] md:relative md:top-auto md:right-auto md:shrink-0 md:translate-x-0 ${
          isDocked ? 'translate-x-0' : 'translate-x-full md:hidden'
        }`}
        style={{ width: `min(100vw, ${sidebarWidth}px)` }}
      >
        <div
          onMouseDown={onDragStart}
          title="Drag to resize"
          className="absolute top-0 left-0 h-full w-1.5 cursor-ew-resize z-10 group hidden md:block"
          style={{ touchAction: 'none' }}
        >
          <div className="h-full w-full bg-transparent group-hover:bg-agent-brand/25 transition-colors duration-150" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-10 rounded-full bg-agent-line-strong group-hover:bg-agent-brand transition-colors duration-150 pointer-events-none" />
        </div>
        <div className="flex-1 overflow-hidden h-full">{isDocked && chat}</div>
      </div>

      {/* Pop-up: a floating window over the page, above the launcher. */}
      {isPopup && (
        <div className="fixed z-[55] inset-x-3 bottom-[calc(124px+env(safe-area-inset-bottom))] top-16 md:inset-x-auto md:top-auto md:right-6 md:bottom-[84px] md:w-[400px] md:h-[min(660px,calc(100vh-120px))] overflow-hidden rounded-[22px] border border-agent-line bg-agent-canvas shadow-agent-float animate-agent-pop motion-reduce:animate-none">
          {chat}
        </div>
      )}

      {isDocked && (
        <div className="fixed inset-0 bg-black/20 z-40 md:hidden" onClick={closeAgent} />
      )}
    </div>
    </AgentPanelProvider>
    </NavigationMenuProvider>
  );
};

export default Layout;
